import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Users, Plus, Search, Edit2, Trash2, Key, Loader2, Shield, Mail, ChevronRight, UserPlus,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  property_manager: 'bg-emerald-100 text-emerald-700',
  viewer: 'bg-slate-100 text-slate-600',
};

export default function MobileUserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'viewer', tenant_id: '' });
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, tenantsRes] = await Promise.all([
        axios.get(`${BACKEND}/api/users`, { headers }),
        axios.get(`${BACKEND}/api/tenants`, { headers }).catch(() => ({ data: { tenants: [] } })),
      ]);
      setUsers(usersRes.data.users || usersRes.data || []);
      setTenants(tenantsRes.data.tenants || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = users.filter(u =>
    !searchQ || u.name?.toLowerCase().includes(searchQ.toLowerCase()) || u.email?.toLowerCase().includes(searchQ.toLowerCase())
  );

  const createUser = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND}/api/users`, form, { headers });
      toast.success(`User ${form.email} created`);
      setCreateOpen(false);
      setForm({ name: '', email: '', password: '', role: 'viewer', tenant_id: '' });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const updateUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND}/api/users/${editUser.id}`, { name: form.name, email: form.email, role: form.role, tenant_id: form.tenant_id }, { headers });
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!resetPwUser || !newPassword) return;
    setSaving(true);
    try {
      await axios.post(`${BACKEND}/api/users/${resetPwUser.id}/reset-password`, { new_password: newPassword }, { headers });
      toast.success(`Password reset for ${resetPwUser.email}`);
      setResetPwUser(null);
      setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND}/api/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="flex flex-col h-full pb-mobile-nav" data-testid="mobile-user-mgmt">
      {/* Header */}
      <div className="shrink-0 p-4 border-b bg-card/50 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold flex items-center gap-2"><Users className="h-5 w-5" /> Users ({users.length})</h2>
          <Button size="sm" className="gap-1.5 h-9" onClick={() => { setCreateOpen(true); setForm({ name: '', email: '', password: '', role: 'viewer', tenant_id: '' }); }} data-testid="create-user-btn">
            <UserPlus className="h-3.5 w-3.5" /> Add User
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search users..." className="pl-9 h-10" />
        </div>
      </div>

      {/* User list */}
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No users found</div>
        ) : (
          <div className="divide-y">
            {filtered.map(u => (
              <div key={u.id} className="p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-muted-foreground">{(u.name || u.email || '?')[0].toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <Badge className={`text-[8px] shrink-0 ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>{u.role}</Badge>
                <div className="flex gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditUser(u); setForm({ name: u.name || '', email: u.email || '', role: u.role || 'viewer', tenant_id: u.tenant_id || '' }); }}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setResetPwUser(u); setNewPassword(''); }}>
                    <Key className="h-3.5 w-3.5 text-amber-600" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteUser(u.id, u.email)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Add User</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Full Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="John Smith" className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="john@company.com" className="mt-1" /></div>
            <div><Label className="text-xs">Password</Label><Input type="text" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Initial password" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="property_manager">Property Manager</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Tenant</Label>
                <Select value={form.tenant_id || '__none__'} onValueChange={v => setForm(p => ({ ...p, tenant_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">All (Super Admin)</SelectItem>
                    {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createUser} disabled={saving || !form.email || !form.password} data-testid="submit-create-user">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-muted-foreground">{editUser?.email}</p>
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="property_manager">Property Manager</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Tenant</Label>
                <Select value={form.tenant_id || '__none__'} onValueChange={v => setForm(p => ({ ...p, tenant_id: v === '__none__' ? '' : v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">All (Super Admin)</SelectItem>
                    {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={updateUser} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwUser} onOpenChange={() => setResetPwUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Key className="h-4 w-4 text-amber-600" /> Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm">Set a new password for <strong>{resetPwUser?.email}</strong></p>
            <div><Label className="text-xs">New Password</Label><Input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter new password" className="mt-1" autoFocus /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetPwUser(null)}>Cancel</Button>
            <Button onClick={resetPassword} disabled={saving || !newPassword} data-testid="submit-reset-password">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Key className="h-4 w-4 mr-1" />} Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
